import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FiClock, FiColumns, FiCornerUpLeft, FiLayers, FiRefreshCw, FiRotateCcw } from "react-icons/fi";
import { parseApiDate } from "../../Helper/dates";
import { getContentVersion, listContentVersions, type ContentVersion } from "./contentVersionsClient";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { useUserDirectory } from "../Account/useUserDirectory";
import { UserAvatar } from "../UserAvatar";
import { Modal } from "../Modal";
import { ChangeDiffViewer } from "../diff/ChangeDiffViewer";

/**
 * "Versions" : the list of saved snapshots of ONE document, newest first. Clicking « Charger cette
 * version » fetches that snapshot's full data and hands it to the parent, which loads it into the
 * local editor state. The operator still has to Save to make the loaded version current (local-only
 * revert), so this panel grants no write on its own.
 */

const relativeTime = (iso?: string | null): string => {
	const date = parseApiDate(iso ?? undefined);
	if (!date) return "";
	const s = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
	if (s < 60) return "à l'instant";
	const m = Math.floor(s / 60);
	if (m < 60) return `il y a ${m} min`;
	const h = Math.floor(m / 60);
	if (h < 24) return `il y a ${h} h`;
	const d = Math.floor(h / 24);
	return d < 7 ? `il y a ${d} j` : date.toLocaleDateString("fr-FR");
};

const ACTION_LABELS: Record<string, string> = {
	created: "Création",
	updated: "Modification",
	deleted: "Suppression",
	restored: "Restauration",
	disabled: "Désactivation",
	enabled: "Réactivation"
};

const actionLabel = (action?: string | null): string => {
	const key = (action ?? "").trim().toLowerCase();
	return ACTION_LABELS[key] ?? "Enregistrement";
};

const authorOf = (v: ContentVersion): string =>
	v.authorEmail && v.authorEmail.trim() && v.authorEmail.trim().toUpperCase() !== "SYSTEM"
		? v.authorEmail.trim()
		: "Système";

const VersionRow: React.FC<{
	version: ContentVersion;
	isCurrent: boolean;
	busy: boolean;
	/** True when an OLDER version is currently loaded in the editor : enables « Revenir à la version actuelle » on the current row. */
	canReturnToCurrent: boolean;
	onLoad: () => void;
	onReturnToCurrent: () => void;
	/** Compare cette version à la version ACTUELLE (absent sur la ligne actuelle). */
	onCompare?: () => void;
	resolveId: (nameOrEmail?: string | null) => string | null;
	resolvePicture: (nameOrEmail?: string | null) => string | null;
}> = ({ version, isCurrent, busy, canReturnToCurrent, onLoad, onReturnToCurrent, onCompare, resolveId, resolvePicture }) => {
	const when = parseApiDate(version.createdAt);
	const raw = version.authorEmail?.trim();
	const isSystem = !raw || raw.toUpperCase() === "SYSTEM";
	const authorId = !isSystem ? resolveId(raw) : null;
	const name = authorOf(version);
	return (
		<li className="px-4 py-2.5">
			<div className="flex items-start gap-3">
				{/* Author avatar, always on the left. */}
				<UserAvatar
					name={name}
					id={authorId ?? name}
					pictureUrl={!isSystem ? resolvePicture(raw) : null}
					sizeClass="mt-0.5 h-6 w-6"
					textClass="text-[9px]"
					title={name}
				/>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center gap-2">
						<span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
							{actionLabel(version.action)}
						</span>
						{isCurrent && (
							<span className="shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
								Version actuelle
							</span>
						)}
						<span className="text-[11px] text-slate-500">
							par{" "}
							{authorId ? (
								<Link to={`/account/${authorId}`} className="font-medium text-slate-600 hover:text-sky-700 hover:underline">
									{name}
								</Link>
							) : (
								name
							)}
						</span>
						<span
							className="inline-flex items-center gap-1 text-[11px] text-slate-400"
							title={when ? when.toLocaleString("fr-FR") : undefined}
						>
							<FiClock className="text-[10px]" /> {relativeTime(version.createdAt)}
						</span>
					</div>
					{version.changeSummary && (
						<p className="mt-0.5 truncate text-sm text-slate-700" title={version.changeSummary}>
							{version.changeSummary}
						</p>
					)}
				</div>
				{isCurrent ? (
					<button
						type="button"
						onClick={onReturnToCurrent}
						disabled={busy || !canReturnToCurrent}
						className="mt-0.5 inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-emerald-700 shadow-sm transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-white"
						title={canReturnToCurrent ? "Rétablir le contenu actuel dans l'éditeur" : "C'est déjà la version affichée"}
					>
						<FiRotateCcw className="text-[11px]" />
						{canReturnToCurrent ? "Revenir à la version actuelle" : "Actuelle"}
					</button>
				) : (
					<div className="mt-0.5 flex shrink-0 items-center gap-1.5">
						{onCompare && (
							<button
								type="button"
								onClick={onCompare}
								disabled={busy}
								className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
								title="Comparer cette version à la version actuelle"
							>
								<FiColumns className="text-[11px]" />
								Comparer
							</button>
						)}
						<button
							type="button"
							onClick={onLoad}
							disabled={busy}
							className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
							title="Charger cette version dans l'éditeur"
						>
							<FiCornerUpLeft className="text-[11px]" />
							Charger cette version
						</button>
					</div>
				)}
			</div>
		</li>
	);
};

export const ContentVersionsPanel: React.FC<{
	contentId?: string | null;
	/** Called with a version's full data once loaded : the parent applies it to local editor state. */
	onLoad: (data: Record<string, any>, version: ContentVersion) => void;
	/** Id of the version currently loaded into the editor (null = showing the live/current content). */
	loadedVersionId?: number | null;
	/** Restore the live/current content into the editor and clear the loaded-version state. */
	onReturnToCurrent?: () => void;
	limit?: number;
	className?: string;
}> = ({ contentId, onLoad, loadedVersionId = null, onReturnToCurrent, limit = 30, className = "" }) => {
	const [versions, setVersions] = useState<ContentVersion[] | null>(null);
	const [loading, setLoading] = useState(false);
	const [busyId, setBusyId] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	// Comparaison « cette version contre l'actuelle » : les deux corps chargés, rendus par le
	// ChangeDiffViewer partagé (le même widget que la page Publication et l'audit).
	const [compare, setCompare] = useState<{ version: ContentVersion; before: unknown; after: unknown } | null>(null);
	const [compareLoading, setCompareLoading] = useState(false);
	const { resolveId, resolvePicture } = useUserDirectory();

	const load = useCallback(async () => {
		if (!contentId) return;
		setLoading(true);
		setError(null);
		try {
			const result = await listContentVersions(contentId, 1, limit);
			setVersions(result.items);
		} catch (e: any) {
			setError(e?.response?.data?.error ?? e?.message ?? "Impossible de charger les versions.");
		} finally {
			setLoading(false);
		}
	}, [contentId, limit]);

	useEffect(() => {
		load();
	}, [load]);

	// Live refresh: a new version is written on every changed save : reload when THIS document is saved
	// (by anyone, including you: this panel is read-only so there's nothing to clobber).
	useRealtimeEvent("content-saved", (evt: any) => {
		if (contentId && evt?.id === contentId) load();
	});

	const handleCompare = useCallback(
		async (version: ContentVersion, current: ContentVersion) => {
			if (!contentId) return;
			setBusyId(version.id);
			setCompareLoading(true);
			setError(null);
			try {
				const [older, latest] = await Promise.all([
					getContentVersion(contentId, version.id),
					getContentVersion(contentId, current.id)
				]);
				setCompare({ version, before: older.data ?? {}, after: latest.data ?? {} });
			} catch (e: any) {
				setError(e?.response?.data?.error ?? e?.message ?? "Impossible de comparer ces versions.");
			} finally {
				setBusyId(null);
				setCompareLoading(false);
			}
		},
		[contentId]
	);

	const handleLoad = useCallback(
		async (version: ContentVersion) => {
			if (!contentId) return;
			setBusyId(version.id);
			setError(null);
			try {
				const detail = await getContentVersion(contentId, version.id);
				onLoad(detail.data ?? {}, version);
			} catch (e: any) {
				setError(e?.response?.data?.error ?? e?.message ?? "Impossible de charger cette version.");
			} finally {
				setBusyId(null);
			}
		},
		[contentId, onLoad]
	);

	if (!contentId) return null;

	return (
		<section className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}>
			<header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
				<div className="flex items-center gap-2">
					<FiLayers className="text-slate-400" />
					<h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">Versions</h3>
					{versions && versions.length > 0 && (
						<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
							{versions.length}
						</span>
					)}
				</div>
				<button
					type="button"
					onClick={load}
					className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600"
					title="Rafraîchir"
				>
					<FiRefreshCw className={loading ? "animate-spin" : ""} />
				</button>
			</header>
			{error && <p className="px-4 py-3 text-sm text-rose-600">{error}</p>}
			{!error && loading && !versions && <p className="px-4 py-4 text-sm text-slate-400">Chargement des versions…</p>}
			{!error && versions && versions.length === 0 && (
				<p className="px-4 py-4 text-sm text-slate-400">
					Aucune version enregistrée : l'historique se remplit à chaque enregistrement modifié.
				</p>
			)}
			{!error && versions && versions.length > 0 && (
				<ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
					{versions.map((version, index) => (
						<VersionRow
							key={version.id}
							version={version}
							isCurrent={index === 0}
							busy={busyId === version.id || compareLoading}
							canReturnToCurrent={loadedVersionId != null && loadedVersionId !== version.id}
							onLoad={() => handleLoad(version)}
							onReturnToCurrent={() => onReturnToCurrent?.()}
							onCompare={index === 0 ? undefined : () => handleCompare(version, versions[0])}
							resolveId={resolveId}
							resolvePicture={resolvePicture}
						/>
					))}
				</ul>
			)}
			{compare && (
				<Modal show onHide={() => setCompare(null)} size="xl" centered>
					<Modal.Header closeButton>
						<Modal.Title>Comparer à la version actuelle</Modal.Title>
					</Modal.Header>
					<Modal.Body scrollable>
						<ChangeDiffViewer
							before={compare.before}
							after={compare.after}
							beforeLabel={`Version du ${parseApiDate(compare.version.createdAt)?.toLocaleString("fr-FR") ?? compare.version.id}`}
							afterLabel="Version actuelle"
						/>
					</Modal.Body>
				</Modal>
			)}
		</section>
	);
};

export default ContentVersionsPanel;
